import { fixed } from '../../math/format';
import { compareSizes, decimalToFraction, fractionToDecimal } from '../../math/units';
import { mcOptions } from '../helpers';
import type { QuestionTemplate } from '../types';

const SIXTEENTH_NAMES = [
  '0', '1/16', '1/8', '3/16', '1/4', '5/16', '3/8', '7/16', '1/2',
  '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', '15/16', '1',
] as const;

/** Reduce n/16 to lowest terms for display. */
function name16(n: number): string {
  return SIXTEENTH_NAMES[n]!;
}

export const FRAC_TEMPLATES: readonly QuestionTemplate[] = [
  {
    id: 'frac.ruler',
    topicId: 'frac',
    skill: 'Reading a rule in sixteenths',
    gen: (rng) => {
      // Skip 0, 8 and 16 — the halves are free and teach nothing.
      const n = rng.pick([1, 2, 3, 5, 6, 7, 9, 10, 11, 13, 14, 15]);
      const solved = fractionToDecimal(n, 16);
      return {
        kind: 'ruler',
        prompt: `Tap ${name16(n)} on the rule.`,
        answerSixteenth: n,
        why: `${name16(n)} is ${n}/16, so count ${n} lines past zero. That is ${fixed(solved.value, 4)}".`,
        steps: [
          { kind: 'note', text: 'Every line on this rule is one sixteenth.' },
          { kind: 'compute', text: `${name16(n)} is the same as ${n}/16.` },
          { kind: 'result', text: `Count ${n} lines from zero.` },
        ],
      };
    },
  },
  {
    id: 'frac.toDecimal',
    topicId: 'frac',
    skill: 'Fraction to decimal',
    gen: (rng) => {
      const den = rng.pick([8, 16, 16, 32]);
      const num = rng.int(1, den - 1);
      const solved = fractionToDecimal(num, den);
      const correct = fixed(solved.value, 4);

      return {
        kind: 'mc',
        prompt: `What is ${num}/${den} as a decimal?`,
        formula: 'top ÷ bottom',
        why: `${num} ÷ ${den} = ${correct}. A fraction is just a division waiting to happen.`,
        steps: solved.steps,
        ...mcOptions(rng, correct, [
          {
            label: fixed(den / num, 4),
            trap: 'That is bottom divided by top. The fraction bar means top ÷ bottom, in that order.',
          },
          {
            label: fixed(solved.value * 2, 4),
            trap: 'Double the right answer — check you did not halve the denominator somewhere.',
          },
          {
            label: fixed(num / (den * 2), 4),
            trap: 'Half the right answer. Read the denominator again.',
          },
        ]),
      };
    },
  },
  {
    id: 'frac.toFraction',
    topicId: 'frac',
    skill: 'Decimal to fraction',
    gen: (rng) => {
      const n = rng.int(1, 15);
      const decimal = n / 16;
      const solved = decimalToFraction(decimal);
      const { numerator, denominator } = solved.value.fraction;

      return {
        kind: 'mc',
        prompt: `${fixed(decimal, 4)} is which fraction?`,
        formula: 'decimal ÷ 0.0625 = number of sixteenths',
        why: `${fixed(decimal, 4)} ÷ 0.0625 = ${n}, so it is ${n}/16 — which reduces to ${numerator}/${denominator}.`,
        steps: solved.steps,
        // Every candidate carries its value, because "1/8" and "2/16" are the
        // same drill and must never both appear.
        ...mcOptions(rng, { label: `${numerator}/${denominator}`, value: decimal }, [
          {
            label: `${numerator}/${denominator * 2}`,
            value: numerator / (denominator * 2),
            trap: 'Right numerator, wrong denominator — that fraction is half the size.',
          },
          {
            label: `${n}/32`,
            value: n / 32,
            trap: 'You counted the lines right but called them thirty-seconds instead of sixteenths.',
          },
          {
            label: `${numerator * 2}/${denominator}`,
            value: (numerator * 2) / denominator,
            trap: 'Twice the size. Check the top number against the count of sixteenths.',
          },
          {
            label: `${denominator}/${numerator}`,
            value: denominator / numerator,
            trap: 'Upside down. The top number is the smaller one here.',
          },
        ]),
      };
    },
  },
  {
    id: 'frac.compare',
    topicId: 'frac',
    skill: 'Comparing a fraction against a decimal',
    gen: (rng) => {
      const den = rng.pick([16, 32]);
      const num = rng.int(3, den - 3);
      const fraction = num / den;
      // A decimal deliberately close enough that eyeballing it fails.
      const offset = rng.pick([-0.008, -0.005, 0.005, 0.008]);
      const decimal = Math.round((fraction + offset) * 1000) / 1000;
      const solved = compareSizes(fraction, decimal);
      const fracIsBigger = solved.value === 1;

      return {
        kind: 'mc',
        prompt: 'Which is bigger?',
        sub: 'You are picking a drill and the print says one, the box says the other.',
        why: `${num}/${den} = ${fixed(fraction, 4)}, and the decimal is ${fixed(decimal, 4)}. The ${fracIsBigger ? 'fraction' : 'decimal'} wins by ${fixed(Math.abs(fraction - decimal), 4)}".`,
        /*
         * Own steps rather than compareSizes' generic ones. The generic
         * version lines both values up as decimals, which for this question
         * is the entire answer — the setup here has to stop at "do this
         * division", not "here are both numbers side by side".
         */
        steps: [
          { kind: 'formula', text: 'Put the fraction into decimals first. Never eyeball one against the other.' },
          { kind: 'substitute', text: `${num}/${den} means ${num} ÷ ${den}` },
          { kind: 'compute', text: `= ${fixed(fraction, 4)}, against ${fixed(decimal, 4)}` },
          ...solved.steps.slice(2),
        ],
        ...mcOptions(rng, fracIsBigger ? `${num}/${den}` : fixed(decimal, 3), [
          {
            label: fracIsBigger ? fixed(decimal, 3) : `${num}/${den}`,
            trap: `Close, but the other way. Put the fraction in decimals first: ${num}/${den} = ${fixed(fraction, 4)}".`,
          },
          {
            label: 'They are the same',
            trap: `They are ${fixed(Math.abs(fraction - decimal), 4)}" apart. That is enough to matter on a fit.`,
          },
        ]),
      };
    },
  },
  {
    id: 'frac.addRule',
    topicId: 'frac',
    skill: 'Adding fractions off a rule',
    gen: (rng) => {
      const a = rng.int(1, 7);
      const b = rng.int(1, 8);
      const sum = a + b;
      const solved = fractionToDecimal(sum, 16);

      return {
        kind: 'ruler',
        prompt: `Tap ${name16(a)} plus ${name16(b)}.`,
        sub: 'Both are already in sixteenths on this rule.',
        answerSixteenth: sum,
        why: `${a}/16 + ${b}/16 = ${sum}/16, which is ${fixed(solved.value, 4)}".`,
        steps: [
          { kind: 'formula', text: 'Same bottom number, so just add the tops.' },
          { kind: 'substitute', text: `${name16(a)} is ${a}/16 and ${name16(b)} is ${b}/16.` },
          { kind: 'compute', text: `${a} + ${b} = ${sum}, so ${sum}/16` },
          { kind: 'result', text: `= ${name16(sum)}, or ${fixed(solved.value, 4)}"` },
        ],
      };
    },
  },
];
